if (!customElements.get('recently-viewed-products')) {
  customElements.define(
    'recently-viewed-products',
    class RecentlyViewedProducts extends HTMLElement {
      static storageKey = 'honey:recently-viewed-products:v1';

      connectedCallback() {
        this.lifecycleController?.abort();
        this.requestController?.abort();
        this.lifecycleController = new AbortController();
        const { signal } = this.lifecycleController;

        document.addEventListener('visitorConsentCollected', () => this.initialize(), { signal });
        this.loadCustomerPrivacy();
      }

      disconnectedCallback() {
        this.lifecycleController?.abort();
        this.requestController?.abort();
      }

      loadCustomerPrivacy() {
        if (typeof window.Shopify?.customerPrivacy?.preferencesProcessingAllowed === 'function') {
          this.initialize();
          return;
        }

        if (typeof window.Shopify?.loadFeatures !== 'function') {
          this.disableHistory();
          return;
        }

        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          if (error) {
            this.disableHistory();
            return;
          }
          this.initialize();
        });
      }

      disableHistory() {
        this.requestController?.abort();
        this.clearHistory();
        this.querySelector('[data-recently-viewed-list]')?.replaceChildren();
        this.hidden = true;
        this.loaded = false;
      }

      async initialize() {
        if (!this.preferencesStorageAllowed()) {
          this.disableHistory();
          return;
        }

        if (this.loading || this.loaded) return;

        const currentHandle = this.cleanHandle(this.dataset.currentProductHandle);
        const history = this.readHistory();
        const handles = history
          .filter((handle) => handle !== currentHandle)
          .slice(0, Number.parseInt(this.dataset.limit, 10) || 4);

        if (currentHandle && !window.Shopify?.designMode) {
          this.writeHistory([currentHandle, ...history.filter((handle) => handle !== currentHandle)]);
        }

        if (!handles.length) return;

        this.loading = true;
        this.requestController = new AbortController();

        try {
          const cards = await Promise.all(handles.map((handle) => this.fetchCard(handle)));
          if (!this.isConnected || !this.preferencesStorageAllowed()) {
            this.disableHistory();
            return;
          }

          const cardMarkup = cards.filter(Boolean);
          if (!cardMarkup.length) return;

          const list = this.querySelector('[data-recently-viewed-list]');
          if (!list) return;

          list.innerHTML = cardMarkup.join('');
          this.hidden = false;
          this.loaded = true;

          const status = this.querySelector('[data-recently-viewed-status]');
          if (status) {
            status.textContent = `${this.dataset.loadedLabel}: ${cardMarkup.length}.`;
          }
        } finally {
          this.loading = false;
        }
      }

      preferencesStorageAllowed() {
        try {
          const privacy = window.Shopify?.customerPrivacy;
          if (typeof privacy?.preferencesProcessingAllowed !== 'function') return false;
          return privacy.preferencesProcessingAllowed();
        } catch (_error) {
          return false;
        }
      }

      readHistory() {
        try {
          const stored = JSON.parse(window.localStorage.getItem(RecentlyViewedProducts.storageKey) || '[]');
          if (!Array.isArray(stored)) return [];
          return [...new Set(stored.map((handle) => this.cleanHandle(handle)).filter(Boolean))].slice(0, 20);
        } catch (_error) {
          return [];
        }
      }

      writeHistory(handles) {
        try {
          window.localStorage.setItem(RecentlyViewedProducts.storageKey, JSON.stringify(handles.slice(0, 20)));
        } catch (_error) {
          // Storage can be unavailable in private browsing or restricted environments.
        }
      }

      clearHistory() {
        try {
          window.localStorage.removeItem(RecentlyViewedProducts.storageKey);
        } catch (_error) {
          // Storage can be unavailable in private browsing or restricted environments.
        }
      }

      cleanHandle(handle) {
        if (typeof handle !== 'string') return '';
        const clean = handle.trim();
        if (!clean || clean.length > 255 || /[/?#\\]/.test(clean)) return '';
        return clean;
      }

      async fetchCard(handle) {
        try {
          const rootUrl = this.dataset.rootUrl.endsWith('/') ? this.dataset.rootUrl : `${this.dataset.rootUrl}/`;
          const url = new URL(`${rootUrl}products/${encodeURIComponent(handle)}`, window.location.origin);
          url.searchParams.set('section_id', this.dataset.sectionId);

          const response = await fetch(url.toString(), {
            credentials: 'same-origin',
            signal: this.requestController.signal,
          });
          if (!response.ok) return '';

          const documentHtml = new DOMParser().parseFromString(await response.text(), 'text/html');
          const cardTemplate = documentHtml.querySelector('template[data-recently-viewed-card-template]');
          const card = cardTemplate?.content.querySelector('[data-recently-viewed-card]');
          return card?.outerHTML || '';
        } catch (error) {
          if (error.name !== 'AbortError') console.warn('Recently viewed product could not be loaded.', error);
          return '';
        }
      }
    }
  );
}
